import React, { useState, useCallback, useRef, useEffect, useMemo } from "react";
import TableOrganism from "../../organisms/Table/TableOrganism";
import {
  ButtonsWrapper,
  FooterButtonsWrapper,
  useStyles,
} from "./AdminTableRequestsPageStyles";
import {
  BigRedButton,
  ButtonVersion,
} from "../../atoms/BigRedButton/BigRedButtonAtom";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from "@material-ui/core";
import { useLocation, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { TableRowProps } from "../../molecules/TableRow/TableRowAtom";
import {
  createInfoCell,
  createCellWithChildren,
  createSelectCell,
  createCurrencyCell,
} from "../../utilities/CreateCell";
import { createRow } from "../../utilities/CreateRow";
import { useLoaderDispatch, useLoaderState } from "../../context/LoaderContext";
import { startLoading, stopLoading } from "../../context/actions/loaderActions";
import Loader from "../../atoms/Loader/LoaderAtom";
import AdminTableRejectDialog from "./AdminTableRejectDialog";
import useApiService from "../../utilities/useApiService";
import { mediumButton } from "../../utilities/style";
import { printStyles } from "../../atoms/PrintButton/PrintButtonAtomStyles";
import { usePharmacyEnabledStatus } from "../../context/PharmacyEnabledStatusContext";
import {
  isFieldProcessorUser,
  isRoleIncluded,
} from "../../utilities/checkUserRole";
import { useAuthState } from "../../context/AuthContext";
import { UserRole } from "../../utilities/Enums";
import PrintHeaderInfo from "../../organisms/PrintHeaderInfo/PrintHeaderInfo";
import { GenerateReportSelector } from "../../atoms/GenerateReportSelector/GenerateReportSelectorAtom";
import { NoteContext } from "../return/ReturnPage";
import EditableModal from "../../atoms/Editable Modal/EditableModal";
import ModalWithText from "../../molecules/ModalWithText/ModalWithText";
import { formatLocalTime } from "../../utilities/DataMatrixParser/formatTime";
import { formatNumberWithCommas } from "../../utilities/formatNumber";
import AppBreadcrumbs from "../../organisms/Breadcrumbs/Breadcrumbs";
import TemporaryNdcModal from "../../organisms/TemporaryNdcAdminModal/TemporaryNdcModal";
import {
  filterPendingTempDrugsForReturnItems,
  TEMP_NDCS_QUERY_KEY,
} from "../../utilities/ndc/temporaryNdcUtils";
import { toastFail } from "../../utilities/toast";
import { AxiosError, AxiosResponse } from "axios";
import { getApiErrorMessage } from "../../utilities/apiErrors";
import { fetchReturnRequestItems } from "../../utilities/returnRequestItems";
import { stripNdcDashes } from "../../utilities/ndc/ndcUtils";
import { isAllPharmaciesReturnsUrl } from "../../utilities/returnRequestsListQuery";

const TEMP_NDC_APPROVE_BLOCKED_MESSAGE =
  "This item uses a temporary NDC that must be reviewed before it can be approved.";

type ItemsPageLocationState = {
  from?: string;
};

const getPutErrorMessage = (result: AxiosResponse | AxiosError): string | null =>
  getApiErrorMessage(result, TEMP_NDC_APPROVE_BLOCKED_MESSAGE);

export interface AdminTableRequestsPageProps {}

type updateStatusArgsType = {
  selectedOption: string;
  itemId: any;
  comment?: string;
};

type UpdateItemStatusResult = {
  updatedItem: any;
  itemId: any;
  comment?: string;
};

let selectOptions = [
  { value: "RETURNABLE", label: "Returnable" },
  { value: "CHEQUE_RECIEVED", label: "Check Received" },
];

const createRequestsRows = (
  data: Array<any>,
  children: any,
  updateStatus: any,
  userRole: string,
) => {
  const rows: Array<TableRowProps> = [];
  data.forEach((entry) => {
    let status = !entry.status ? entry.itemStatus : entry.status;
    const isFull = entry.fullQuantity !== 0;
    const statusCell =
      (status === "RETURNABLE" || status === "CHEQUE_RECIEVED") &&
      isRoleIncluded([UserRole.Admin, UserRole.Warehouse], userRole)
        ? createSelectCell(selectOptions, status, (event: any) => {
            updateStatus({
              selectedOption: event.target.value,
              itemId: entry.id,
            });
          })
        : createInfoCell(status);

    const approveRejectCell = 
      isRoleIncluded([UserRole.Admin, UserRole.Warehouse], userRole)
          ? createCellWithChildren(children(status, entry))
          : null;
      
    const row: TableRowProps = createRow(...[
      createInfoCell(entry.ndc),
      createInfoCell(entry.description),
      createInfoCell(entry.manufacturer),
      createInfoCell(entry.strength),
      createInfoCell(entry.dosage),
      createInfoCell(entry.packageSize),
      createInfoCell(entry.gtin14),
      createInfoCell(entry.serialNumber),
      createInfoCell(entry.controlledSubstanceCode),
      createInfoCell(
        entry.fullQuantity
          .toString(10)
          .concat(
            entry.fullQuantity !== 0
              ? entry.isFullQuantityOpened
                ? " F/O"
                : " F/S"
              : ""
          )
      ),
      createInfoCell(
        entry.partialQuantity
          .toString(10)
          .concat(
            entry.partialQuantity !== 0
              ? (() => {
                  const ps = entry.packageSize;
                  if (ps == null || ps === "") {
                    return "";
                  }
                  const parts = String(ps).trim().split(/\s+/);
                  return parts.length > 1 ? " " + parts.slice(1).join(" ") : "";
                })()
              : "",
          ),
      ),
      createCurrencyCell(
        isFull
          ? (parseFloat(entry.extendedActualPrice) !== 0 ? entry.extendedActualPrice : parseFloat(entry.extendedPrice) * 0.7)
          : (parseFloat(entry.extendedUnitActualPrice) !== 0 ? entry.extendedUnitActualPrice : parseFloat(entry.extendedUnitPrice) * 0.7)
      ),
      statusCell,
      approveRejectCell
    ].filter(Boolean));
    rows.push(row);
  });
  return rows;
};

const AdminTableRequestsPage: React.FC<AdminTableRequestsPageProps> = (
  props,
) => {
  const { getMethod, postMethod, putMethod } = useApiService();
  const { user } = useAuthState();
  const userRole = user!.role;
  const params: any = useParams();
  const loaderDispatch = useLoaderDispatch();
  const { loading } = useLoaderState();
  const [openReject, setOpenReject] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const { pharmacyEnabledStatusMap } = usePharmacyEnabledStatus();
  const queryClient = useQueryClient();
  const labelRef = useRef(null);
  const classes = useStyles({});

  const [openNoteModal, setOpenNoteModal] = useState(false);
  const [noteContext, setNoteContext] = useState<NoteContext | null>(null);
  const [openTempNdcModal, setOpenTempNdcModal] = useState(false);
  const [showTempWarning, setShowTempWarning] = useState(false);
  const [pendingTempCount, setPendingTempCount] = useState(0);
  const tempWarningDismissedRef = useRef(false);
  const location = useLocation<ItemsPageLocationState>();

  const getReturnRequestHeader = async () => {
    let response = await getMethod(
      `/pharmacies/${params.pharmacyId}/returnrequests/${params.requestId}`,
    );
    return response.data;
  };

  const { data: returnRequestDetails } = useQuery(
    ["returnRequestHeader", params.requestId],
    getReturnRequestHeader,
    { enabled: !!params.requestId },
  );

  const getItems = async () => {
    startLoading(loaderDispatch);
    try {
      return await fetchReturnRequestItems(
        getMethod,
        params.pharmacyId,
        params.requestId,
      );
    } finally {
      stopLoading(loaderDispatch);
    }
  };

  const { data: items = [] } = useQuery("items", getItems);

  const markNoteAsSeen = useCallback(
    async (id: number) => {
      await putMethod(`/pharmacies/note/${id}`);
      await queryClient.invalidateQueries([
        "returnRequestHeader",
        params.requestId,
      ]);
    },
    [putMethod, queryClient, params.requestId],
  );

  const handleClickNote = async (entry: any, userRole: string) => {
    const rawNote = entry.note || null;
    setOpenNoteModal(true);

    if (rawNote && !rawNote.seen && !isFieldProcessorUser(userRole)) {
      setTimeout(() => {
        markNoteAsSeen(rawNote.id);
      }, 300);
    }

    const cleanNote = rawNote
      ? {
          id: rawNote.id,
          text: rawNote.text,
          seen: rawNote.seen,
        }
      : null;

    setNoteContext({
      note: cleanNote,
      returnRequestId: entry.id,
      pharmacyId: entry.pharmacy.id,
      userRole,
    });
  };

  const handleSaveNote = async (editedNote: string) => {
    if (!noteContext) return null;
    await putMethod(
      `/pharmacies/${noteContext.pharmacyId}/returnrequests/${noteContext.returnRequestId}/note`,
      {
        text: editedNote,
      },
    );
    handleCloseModal();
    await queryClient.invalidateQueries([
      "returnRequestHeader",
      params.requestId,
    ]);
  };

  const handleCloseModal = () => {
    setOpenNoteModal(false);
    setNoteContext(null);
  };

  const renderNoteModal = () => {
    if (!noteContext) return null;
    const noteText = noteContext.note?.text || "";
    if (isFieldProcessorUser(noteContext.userRole)) {
      return (
        <EditableModal
          open={openNoteModal}
          selectedNote={noteText}
          saveNote={handleSaveNote}
          onCloseModal={handleCloseModal}
        />
      );
    } else {
      return (
        <ModalWithText
          open={openNoteModal}
          content={noteText}
          onClose={handleCloseModal}
        />
      );
    }
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const shouldOpenNote = searchParams.get("openNote") === "true";
    if (!returnRequestDetails) return;
    if (shouldOpenNote && returnRequestDetails.note && !openNoteModal) {
      handleClickNote(returnRequestDetails, userRole);
    }
  }, [location.search, returnRequestDetails, userRole]);

  const getPendingTempNdcs = async () => {
    const res = await getMethod("/temporary-ndcs");
    return res.data;
  };

  const canManageReturnItems = isRoleIncluded(
    [UserRole.Admin, UserRole.Warehouse],
    userRole,
  );

  const isAdmin = isRoleIncluded([UserRole.Admin], userRole);

  const { data: pendingTempNdcs = [], isFetched: isPendingTempNdcsFetched } =
    useQuery(TEMP_NDCS_QUERY_KEY, getPendingTempNdcs, {
      enabled: canManageReturnItems,
      refetchOnMount: true,
      staleTime: 0,
    });

  const pendingTempNdcSet = useMemo(
    () =>
      new Set(
        pendingTempNdcs.map((entry: { blockingNdc?: string; ndc: string }) =>
          stripNdcDashes(entry.blockingNdc ?? entry.ndc),
        ),
      ),
    [pendingTempNdcs],
  );

  const hasPendingTemporaryNdc = useCallback(
    (ndc: string) => pendingTempNdcSet.has(stripNdcDashes(ndc)),
    [pendingTempNdcSet],
  );

  const returnRequestTempNdcs = useMemo(
    () => filterPendingTempDrugsForReturnItems(pendingTempNdcs, items),
    [pendingTempNdcs, items],
  );

  const showReturnTempNdcButton = returnRequestTempNdcs.length > 0;

  const putUpdateStatus = async ({
    selectedOption,
    itemId,
    comment,
  }: updateStatusArgsType): Promise<UpdateItemStatusResult> => {
    const response = await putMethod<any>(
      `/pharmacies/${params.pharmacyId}/returnrequests/${params.requestId}/items/${itemId}/status`,
      {
        valid: selectedOption,
      },
    );
    const errorMessage = getPutErrorMessage(
      response as AxiosResponse | AxiosError,
    );
    if (errorMessage) {
      toastFail(errorMessage);
      throw new Error(errorMessage);
    }
    return {
      updatedItem: (response as AxiosResponse<any>).data,
      itemId,
      comment,
    };
  };
  const handleApproveAll = async () => {
    if (!items || items.length === 0) return;
    const pendingItems = items.filter((item: any) => item.status === "PENDING");
    if (pendingItems.length === 0) {
      return;
    }
    const blockedByTempNdc = pendingItems.some((item: any) =>
      hasPendingTemporaryNdc(item.ndc),
    );
    if (blockedByTempNdc) {
      toastFail(TEMP_NDC_APPROVE_BLOCKED_MESSAGE);
      return;
    }
    const pendingItemIds = pendingItems.map((item: any) => item.id);
    startLoading(loaderDispatch);
    try {
      const response = await putMethod(
        `/pharmacies/${params.pharmacyId}/returnrequests/${params.requestId}/items/bulk/status`,
        {
          itemIds: pendingItemIds,
          status: "RETURNABLE",
        },
      );
      const errorMessage = getPutErrorMessage(
        response as AxiosResponse | AxiosError,
      );
      if (errorMessage) {
        toastFail(errorMessage);
        return;
      }
      const updatedItems = items.map((item: any) => ({
        ...item,
        status: pendingItemIds.includes(item.id) ? "RETURNABLE" : item.status,
      }));
      await queryClient.setQueryData("items", updatedItems);
    } catch (error) {
      console.error("Error approving all items:", error);
    } finally {
      stopLoading(loaderDispatch);
    }
  };

  const { mutate: updateStatus } = useMutation<
    UpdateItemStatusResult,
    Error,
    updateStatusArgsType
  >(putUpdateStatus, {
    onSuccess: async ({ updatedItem, itemId, comment }) => {
      if (
        updatedItem?.status === "N_RETURNABLE" ||
        updatedItem?.itemStatus === "N_RETURNABLE"
      ) {
        addComment({
          comment,
          itemId,
        });
      } else {
        updateCachedData({ updatedItem, itemId });
      }
    },
  });

  const postComment = async ({ comment, itemId }: any) => {
    const response = await postMethod(
      `/pharmacies/${params.pharmacyId}/returnrequests/${params.requestId}/items/${itemId}/comments`,
      {
        comment,
      },
    );
    return { updatedItem: response.data, itemId };
  };
  const { mutate: addComment } = useMutation(postComment, {
    onSuccess: ({ updatedItem, itemId }) => {
      updateCachedData({ updatedItem, itemId });
    },
  });

  const updateCachedData = async ({ updatedItem, itemId }: any) => {
    const newItems = items.map((obj: any) => {
      let returnObj = { ...obj };
      if (returnObj.id === itemId) {
        returnObj = updatedItem;
      }
      return returnObj;
    });
    console.log("New data", newItems);
    await queryClient.setQueryData("items", newItems);
  };

  const renderChildren = useCallback(
    (status = "PENDING", item) => {
      const pharmacyId = Number(params.pharmacyId);
      const isPharmacyDisabled = pharmacyEnabledStatusMap[pharmacyId] === false;
      const isTempNdcPending = hasPendingTemporaryNdc(item.ndc);
      const isApproveDisabled = isPharmacyDisabled || isTempNdcPending;
      return status !== "PENDING" ? (
        <ButtonsWrapper className="exclude-print"></ButtonsWrapper>
      ) : (
        <ButtonsWrapper className="exclude-print">
          {isRoleIncluded([UserRole.Admin, UserRole.Warehouse], userRole) && (
            <Button
              onClick={() => {
                setSelectedItem(item);
                updateStatus({
                  selectedOption: "RETURNABLE",
                  itemId: item.id,
                });
              }}
              disabled={isApproveDisabled}
              title={
                isTempNdcPending ? TEMP_NDC_APPROVE_BLOCKED_MESSAGE : undefined
              }
              className="approveButton"
            >
              APPROVE
            </Button>
          )}
          {isRoleIncluded([UserRole.Admin, UserRole.Warehouse], userRole) && (
            <BigRedButton
              text="REJECT"
              version={ButtonVersion.SMALL}
              onClick={() => {
                setOpenReject(true);
                setSelectedItem(item);
              }}
              disabled={isPharmacyDisabled}
            />
          )}
        </ButtonsWrapper>
      );
    },
    [
      params.pharmacyId,
      pharmacyEnabledStatusMap,
      updateStatus,
      userRole,
      hasPendingTemporaryNdc,
    ],
  );

  const hasPendingTempNdcsOnReturnItems = useMemo(
    () =>
      items?.some(
        (item: any) =>
          item.status === "PENDING" && hasPendingTemporaryNdc(item.ndc),
      ) ?? false,
    [items, hasPendingTemporaryNdc],
  );

  const getReportsButton = () => {
    if (!items || items.length === 0) return null;

    const hasPendingItems = items.some(
      (item: any) => item.status === "PENDING",
    );

    const isAdminOrWarehouse = isRoleIncluded(
      [UserRole.Admin, UserRole.Warehouse],
      userRole,
    );

    const isPharmacyOrFieldProcessor = isRoleIncluded(
      [UserRole.User, UserRole.FieldProcessor],
      userRole,
    );

    const initialReports = items.reduce(
      (reports: string[], item: any) => {
        let report = undefined;
        if (item.controlledSubstanceCode?.match("3|4|5"))
          report = "SCHEDULE-345";
        if (item.controlledSubstanceCode?.match("2")) report = "SCHEDULE-2";

        if (report && !reports.includes(report)) reports.push(report);
        return reports;
      },
      ["INITIAL-REPORT"],
    );

    const returnReports: string[] = items.reduce(
      (reports: string[], item: any) => {
        let report = undefined;
        if (item.controlledSubstanceCode === "2") report = "SCHEDULE-2";
        else if (item.controlledSubstanceCode?.match("3|4|5"))
          report = "SCHEDULE-345";

        if (report && !reports.includes(report)) reports.push(report);
        return reports;
      },
      ["RETURN-REPORT"],
    );

    if (isAdminOrWarehouse) {
      if (hasPendingItems) {
        return (
          <GenerateReportSelector
            buttonText="Generate Initial Report"
            buttonHeight="80px"
            buttonWidth={mediumButton.width}
            availableReports={initialReports}
            documentType="pdf"
            hideReports={["RETURN-REPORT", "SCHEDULE-INITIAL"]}
          />
        );
      }

      return (
        <GenerateReportSelector
          buttonText="Generate Report"
          buttonHeight="80px"
          buttonWidth={mediumButton.width}
          availableReports={hasPendingItems ? [] : returnReports}
          documentType="excel"
          hideReports={["SCHEDULE-INITIAL", "INITIAL-REPORT"]}
        />
      );
    }
    if (isPharmacyOrFieldProcessor && hasPendingItems) {
      return (
        <GenerateReportSelector
          buttonText="Generate Initial Report"
          buttonHeight="80px"
          buttonWidth={mediumButton.width}
          availableReports={initialReports}
          documentType="pdf"
          hideReports={["RETURN-REPORT", "SCHEDULE-INITIAL"]}
        />
      );
    }
    return null;
  };

  const rows: Array<TableRowProps> = items
    ? createRequestsRows(items, renderChildren, updateStatus, userRole)
    : [];

  const header = {
    cells: [
      {
        bold: false,
        clickable: false,
        header: true,
        onClick: () => {},
        text: "NDC",
      },
      {
        bold: false,
        clickable: false,
        header: true,
        onClick: () => {},
        text: "Description",
      },
      {
        bold: false,
        clickable: false,
        header: true,
        onClick: () => {},
        text: "Manufacturer",
      },
      {
        bold: false,
        clickable: false,
        header: true,
        onClick: () => {},
        text: "Strength",
      },
      {
        bold: false,
        clickable: false,
        header: true,
        onClick: () => {},
        text: "Dosage",
      },
      {
        bold: false,
        clickable: false,
        header: true,
        onClick: () => {},
        text: "Pkg Size",
      },
      {
        bold: false,
        clickable: false,
        header: true,
        onClick: () => {},
        text: "GTIN",
      },
      {
        bold: false,
        clickable: false,
        header: true,
        onClick: () => {},
        text: "Serial #",
      },
      {
        bold: false,
        clickable: false,
        header: true,
        onClick: () => {},
        text: "CSC",
      },
      {
        bold: false,
        clickable: false,
        header: true,
        onClick: () => {},
        text: "Full Qty",
      },
      {
        bold: false,
        clickable: false,
        header: true,
        onClick: () => {},
        text: "Partial Qty",
      },
      {
        bold: false,
        clickable: false,
        header: true,
        onClick: () => {},
        text: "Estimated Price",
      },
      {
        bold: false,
        clickable: false,
        header: true,
        onClick: () => { },
        text: "Status",
      },
      isRoleIncluded([UserRole.Admin, UserRole.Warehouse], userRole)
        ? {
            bold: false,
            clickable: false,
            header: true,
            onClick: () => {},
            text: "",
            className: "exclude-print",
          }
        : null,
    ].filter(Boolean) as any[],
  };

  const renderPharmacyInfo = () => {
    if (items && items.length > 0) {
      return (
        <PrintHeaderInfo
          pharmacyName={items[0].pharmacyDoingBusinessAs}
          createdAt={formatLocalTime(items[0].returnRequestCreatedAt)}
        />
      );
    }
    return null;
  };

  const rawTotal = items
    ? items.reduce(
        (sum: number, item: any) =>
          sum +
          (item.extendedActualPrice &&
          parseFloat(item.extendedActualPrice) !== 0
            ? parseFloat(item.extendedActualPrice)
            : parseFloat(item.extendedPrice) * 0.7 || 0) +
          (item.extendedUnitActualPrice &&
          parseFloat(item.extendedUnitActualPrice) !== 0
            ? parseFloat(item.extendedUnitActualPrice)
            : parseFloat(item.extendedUnitPrice) * 0.7 || 0),
        0,
      )
    : 0;

  const totalSum = formatNumberWithCommas(rawTotal, true);

  const pharmacyName = items?.[0]?.pharmacyDoingBusinessAs || "Pharmacy";
  const fromListUrl = location.state?.from;
  const isAdminPath = location.pathname.startsWith("/admin");
  const defaultReturnsPath = isAdminPath
    ? `/admin/pharmacies/${params.pharmacyId}/returns`
    : `/pharmacies/${params.pharmacyId}/returns`;
  const returnsListPath = fromListUrl || defaultReturnsPath;

  const breadcrumbItems = [
    {
      label: pharmacyName,
      path: isAdminPath
        ? `/admin/pharmacies/${params.pharmacyId}`
        : `/pharmacies/${params.pharmacyId}`,
    },
    {
      label: isAllPharmaciesReturnsUrl(fromListUrl)
        ? "Return Requests"
        : "Pharmacy Returns",
      path: returnsListPath,
    },
    {
      label: "Items",
    },
  ];

  useEffect(() => {
    if (!canManageReturnItems || !isPendingTempNdcsFetched || !items) return;
    const count = returnRequestTempNdcs.length;
    if (count > 0) {
      setPendingTempCount(count);
      if (!tempWarningDismissedRef.current && !openTempNdcModal) {
        setShowTempWarning(true);
      }
    } else {
      setShowTempWarning(false);
    }
  }, [
    canManageReturnItems,
    isPendingTempNdcsFetched,
    returnRequestTempNdcs,
    items,
    openTempNdcModal,
  ]);

  return loading ? (
    <Loader />
  ) : (
    <>
      <Dialog
        open={showTempWarning}
        onClose={() => {
          tempWarningDismissedRef.current = true;
          setShowTempWarning(false);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle style={{ color: "#A61C14", fontWeight: 600 }}>
          Temporary NDC Warning
        </DialogTitle>

        <DialogContent>
          ⚠ This return has {pendingTempCount} Temporary NDC(s) pending review.
          <br />
          {isAdmin
            ? "Please review them before processing returns."
            : "Please have an admin review them before processing returns."}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => {
              tempWarningDismissedRef.current = true;
              setShowTempWarning(false);
            }}
          >
            Ignore
          </Button>

          {isAdmin && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                tempWarningDismissedRef.current = true;
                setShowTempWarning(false);
                setOpenTempNdcModal(true);
              }}
            >
              Open Management
            </Button>
          )}
        </DialogActions>
      </Dialog>
      <div style={{ marginBottom: 40 }} ref={labelRef}>
        {renderPharmacyInfo()}
        <div className={classes.breadcrumbsContainer}>
          <AppBreadcrumbs items={breadcrumbItems} />
        </div>
        <h1 className="print-title">Expected Returns</h1>
        <style>{printStyles}</style>
        <TableOrganism
          rows={rows}
          header={header}
          tableClassName="print-table"
        />
        <FooterButtonsWrapper className="exclude-print">
          {isAdmin && showReturnTempNdcButton && (
            <Button
              onClick={() => setOpenTempNdcModal(true)}
              variant="contained"
              color="primary"
              style={{ marginRight: 12 }}
            >
              Temporary NDCs
            </Button>
          )}
          {isRoleIncluded([UserRole.Admin, UserRole.Warehouse], userRole) &&
            items?.some((item: any) => item.status === "PENDING") && (
              <Button
                onClick={handleApproveAll}
                className="approveButton"
                disabled={
                  pharmacyEnabledStatusMap[Number(params.pharmacyId)] ===
                    false || hasPendingTempNdcsOnReturnItems
                }
                title={
                  hasPendingTempNdcsOnReturnItems
                    ? TEMP_NDC_APPROVE_BLOCKED_MESSAGE
                    : undefined
                }
              >
                APPROVE ALL
              </Button>
            )}
        </FooterButtonsWrapper>
        <h2 className="total">Total: {totalSum}</h2>
      </div>
      <AdminTableRejectDialog
        open={openReject}
        selectedItem={selectedItem}
        updateStatus={updateStatus}
        addComment={addComment}
        onClose={() => {
          setOpenReject(false);
          setSelectedItem(null);
        }}
      />
      {getReportsButton()}
      {renderNoteModal()}

      <TemporaryNdcModal
        open={openTempNdcModal}
        onClose={() => {
          setOpenTempNdcModal(false);
          queryClient.invalidateQueries(TEMP_NDCS_QUERY_KEY);
        }}
        returnItems={items}
      />
    </>
  );
};

export default AdminTableRequestsPage;
